import clsx from "classnames";
import React from "react";

import sharedStyles from "../../styles/shared/inputs.module.css";
import styles from "./index.module.css";

interface ToggleProps extends Omit<React.ComponentProps<"input">, "disabled"> {
  /** If Toggle is on */
  isChecked?: boolean;
  /** Disabled state */
  isDisabled?: boolean;
  /** Label for the Toggle, can contain HTML */
  children?: React.ReactNode;
  /** Description for screen readers when additional context is needed */
  "aria-describedby"?: string;
  /** Label for screen readers when children is not sufficient */
  "aria-label"?: string;
  /** Indicates if the toggle has a validation error */
  "aria-invalid"?: boolean;
}

/**
 * A styled checkbox for on/off states.
 */

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      className,
      isChecked,
      isDisabled,
      children,
      "aria-describedby": ariaDescribedby,
      "aria-label": ariaLabel,
      "aria-invalid": ariaInvalid,
      ...rest
    },
    ref
  ) => {
    const classes = clsx(
      styles.root,
      {
        [sharedStyles.isDisabled]: isDisabled,
      },
      className
    );

    const hiddenInputClasses = clsx(
      styles.hiddenInput,
      sharedStyles.hiddenInput
    );

    return (
      <label className={classes}>
        <div className={styles.labelText}>{children}</div>
        <div>
          <input
            type="checkbox"
            role="switch"
            aria-checked={isChecked}
            aria-describedby={ariaDescribedby}
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid}
            className={hiddenInputClasses}
            disabled={isDisabled}
            checked={isChecked}
            ref={ref}
            {...rest}
          />
          <div className={styles.uiElement}>
            <div className={styles.uiElementInner}></div>
          </div>
        </div>
      </label>
    );
  }
);

Toggle.displayName = "Toggle";
